// Вендоренная копия FileSaver.js (https://github.com/eligrey/FileSaver.js), MIT.
// Внутренности не менялись; добавлена только типизация границы. Публичная сигнатура saveAs
// соответствует @types/file-saver. Нестандартные браузерные свойства — узкие касты к локальным типам.

interface FileSaverOptions {
  autoBom: boolean;
}

type SaveAs = (data: Blob | string, filename?: string, options?: FileSaverOptions) => void;

interface LegacyWindow extends Window {
  URL: typeof URL;
  HTMLElement: typeof HTMLElement;
  webkitURL?: typeof URL;
  safari?: unknown;
  saveAs?: SaveAs;
}

interface LegacyNavigator extends Navigator {
  msSaveOrOpenBlob?: (blob: Blob, name?: string) => boolean;
}

// typeof-гард обязателен: модуль загружается и в node-окружении тестов, где window не определён.
const _global = (typeof window === 'object' ? window : {}) as unknown as LegacyWindow;

function bom(blob: Blob, opts?: FileSaverOptions | boolean): Blob {
  let options: FileSaverOptions;
  if (typeof opts === 'undefined') options = { autoBom: false };
  else if (typeof opts !== 'object') {
    console.warn('Deprecated: Expected third argument to be a object');
    options = { autoBom: !opts };
  } else {
    options = opts;
  }

  if (options.autoBom && /^\s*(?:text\/\S*|application\/xml|\S*\/\S*\+xml)\s*;.*charset\s*=\s*utf-8/i.test(blob.type)) {
    return new Blob([String.fromCharCode(0xfeff), blob], { type: blob.type });
  }
  return blob;
}

function download(url: string, name?: string, opts?: FileSaverOptions): void {
  const xhr = new XMLHttpRequest();
  xhr.open('GET', url);
  xhr.responseType = 'blob';
  xhr.onload = function () {
    saveAs(xhr.response, name, opts);
  };
  xhr.onerror = function () {
    console.error('could not download file');
  };
  xhr.send();
}

function corsEnabled(url: string): boolean {
  const xhr = new XMLHttpRequest();

  xhr.open('HEAD', url, false);
  try {
    xhr.send();
  } catch (e) {
    console.error(e);
  }
  return xhr.status >= 200 && xhr.status <= 299;
}

function click(node: HTMLElement): void {
  try {
    node.dispatchEvent(new MouseEvent('click'));
  } catch {
    const evt = document.createEvent('MouseEvents');
    evt.initMouseEvent('click', true, true, window, 0, 0, 0, 80, 20, false, false, false, false, 0, null);
    node.dispatchEvent(evt);
  }
}

const isMacOSWebView =
  _global.navigator &&
  /Macintosh/.test(navigator.userAgent) &&
  /AppleWebKit/.test(navigator.userAgent) &&
  !/Safari/.test(navigator.userAgent);

export const saveAs = (_global.saveAs ||
  (typeof window !== 'object' || window !== _global
    ? function saveAs() {
        /* noop */
      }
    : 'download' in HTMLAnchorElement.prototype && !isMacOSWebView
      ? function saveAs(blob: Blob | string, name?: string, opts?: FileSaverOptions) {
          const URL = _global.URL || _global.webkitURL;
          const a = document.createElement('a');
          name = name || (blob as File).name || 'download';

          a.download = name;
          a.rel = 'noopener'; // tabnabbing

          if (typeof blob === 'string') {
            a.href = blob;
            if (a.origin !== location.origin) {
              if (corsEnabled(a.href)) {
                download(blob, name, opts);
              } else {
                a.target = '_blank';
                click(a);
              }
            } else {
              click(a);
            }
          } else {
            a.href = URL.createObjectURL(blob);
            setTimeout(function () {
              URL.revokeObjectURL(a.href);
            }, 4e4); // 40s
            setTimeout(function () {
              click(a);
            }, 0);
          }
        }
      : 'msSaveOrOpenBlob' in navigator
        ? function saveAs(blob: Blob | string, name?: string, opts?: FileSaverOptions) {
            name = name || (blob as File).name || 'download';

            if (typeof blob === 'string') {
              if (corsEnabled(blob)) {
                download(blob, name, opts);
              } else {
                const a = document.createElement('a');
                a.href = blob;
                a.target = '_blank';
                setTimeout(function () {
                  click(a);
                });
              }
            } else {
              (navigator as LegacyNavigator).msSaveOrOpenBlob!(bom(blob, opts), name);
            }
          }
        : function saveAs(blob: Blob | string, name?: string, opts?: FileSaverOptions, popup?: Window | null) {
            popup = popup || open('', '_blank');
            if (popup) {
              popup.document.title = popup.document.body.innerText = 'downloading...';
            }

            if (typeof blob === 'string') return download(blob, name, opts);

            const force = blob.type === 'application/octet-stream';
            const isSafari = /constructor/i.test(String(_global.HTMLElement)) || Boolean(_global.safari);
            const isChromeIOS = /CriOS\/[\d]+/.test(navigator.userAgent);

            if ((isChromeIOS || (force && isSafari) || isMacOSWebView) && typeof FileReader !== 'undefined') {
              const reader = new FileReader();
              reader.onloadend = function () {
                let url = reader.result as string;
                url = isChromeIOS ? url : url.replace(/^data:[^;]*;/, 'data:attachment/file;');
                if (popup) popup.location.href = url;
                else location.href = url;
                popup = null;
              };
              reader.readAsDataURL(blob);
            } else {
              const URL = _global.URL || _global.webkitURL;
              const url = URL.createObjectURL(blob);
              if (popup) popup.location.href = url;
              else location.href = url;
              popup = null;
              setTimeout(function () {
                URL.revokeObjectURL(url);
              }, 4e4);
            }
          })) as SaveAs;

_global.saveAs = saveAs;
